<template>
  <section class="log-in-page">
    <form class="inner-block" novalidate @submit.prevent="submit">
      <loading-spinner v-if="pageLoading" class="loading-spinner" />
      <div v-if="!pageLoading">
        <slot />
        <h1>Log in</h1>
        <div
          :class="{
            'form-group-error': $v.email.$error,
            'form-group': true,
            email: true,
          }"
        >
          <input
            id="email"
            v-model.trim="$v.email.$model"
            type="text"
            :disabled="isLoading"
            placeholder="Email"
            autocomplete="email"
            @change="onFieldChange($v.email)"
          />
          <div v-if="!$v.email.required" class="error">Email is required</div>
          <div v-if="!$v.email.email" class="error">
            Email must be a valid email address
          </div>
        </div>
        <div
          :class="{
            'form-group-error': $v.password.$error,
            'form-group': true,
            password: true,
          }"
        >
          <input
            id="password"
            v-model.trim="$v.password.$model"
            type="password"
            placeholder="Password"
            :disabled="isLoading"
            autocomplete="password"
            @change="onFieldChange($v.password)"
          />
          <div v-if="!$v.password.required" class="error">
            Password is required
          </div>
        </div>
        <alert v-if="error">
          {{ error }}
        </alert>
        <cta-button
          type="submit"
          class="button"
          :loading="isLoading"
          :solid="true"
          >Start with email</cta-button
        >
        <div class="section-forgot">
          <span>Forgot your</span>
          <button
            class="forgot-password"
            type="button"
            @click="forgotPasswordHandler"
          >
            password
          </button>
          <span>or </span>
          <a class="forgot-password" type="button" :href="mailForgotEmail"
            >email</a
          >
          <span>?</span>
        </div>
      </div>
    </form>
  </section>
</template>

<script>
import { required, email } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import nextRouteMixin from "@/mixins/nextRoute";
import CtaButton from "@/components/CtaButton";
import Alert from "@/components/Alert";
import LoadingSpinner from "@/components/LoadingSpinner";

const GENERIC_ERROR =
  "Something went wrong when logging you in! Please try again or use the link below to reset your password.";

export default {
  name: "LogInPage",
  mixins: [validationMixin, nextRouteMixin],
  components: {
    CtaButton,
    Alert,
    LoadingSpinner,
  },
  data() {
    return {
      password: null,
      email: null,
      isLoading: false,
      error: null,
      pageLoading: true,
      helpMail: "mailto:go-support@talkspace.com",
    };
  },
  computed: {
    mailForgotEmail() {
      const subject = "Help with account.getlasting.com - Forgot Email";
      const newLineString = "%0D%0A";
      const body = `Thanks for reaching out to support. Simply send this email and we'll help you locate your account.${newLineString} ${newLineString}
The simplest way to locate your email is by opening your Lasting app, tapping on the Profile tab, then tapping on Account. There, you'll see your account email.${newLineString} ${newLineString}
If you're unsure of which email you used, feel free to list any emails you may have used to create an account below. We can search for them in our system.`;
      return `${this.helpMail}?subject=${subject}&body=${body}`;
    },
  },
  validations: {
    email: { required, email },
    password: { required },
  },
  mounted() {
    if (this.$user.isLoggedIn()) {
      if (this.$user.user) {
        this.pageLoading = false;
        this.profileLoaded();
      } else {
        this.$user
          .getProfile()
          .then(this.profileLoaded)
          .catch(this.autoLoadProfileError);
      }
    } else {
      this.pageLoading = false;
      if (this.$router.currentRoute.query.login) {
        this.email = decodeURIComponent(this.$router.currentRoute.query.login);
      }
    }
  },
  methods: {
    submit() {
      this.error = null;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.error =
          "There was a problem with your submission. Please correct the errors in the form above.";
        return;
      }
      this.isLoading = true;
      this.$user
        .logIn(this.email, this.password)
        .then(this.logInComplete)
        .catch(this.handleLogInError);
    },
    autoLoadProfileError() {
      this.pageLoading = false;
    },
    logInComplete() {
      this.$user
        .getProfile()
        .then(this.profileLoaded)
        .catch(this.handleLoadProfileError);
    },
    handleLogInError(error) {
      if (error && error.response && error.response.status === 404) {
        this.error =
          "The email or password you entered is incorrect. Please try again or use the link below to reset your password.";
      } else {
        this.error = GENERIC_ERROR;
      }
      this.isLoading = false;
    },
    profileLoaded() {
      const subscription = this.$user.user["subscription-info"];
      let data = null;

      if (
        subscription &&
        subscription.status &&
        subscription.status === "Premium" &&
        subscription["next-billing-amount"]
      ) {
        data = {
          stripe_price_id: `price_${subscription["next-billing-amount"]}`,
        };
      }

      this.$tracker.identify(this.$user.user.id);
      this.$tracker.trackLogIn(data);
      if (this.$router.currentRoute.query.redirect) {
        this.$router
          .push({ path: this.$router.currentRoute.query.redirect })
          .catch(() => {});
        return;
      }
      this.goToNextRoute();
    },
    handleLoadProfileError() {
      this.error = GENERIC_ERROR;
      this.isLoading = false;
    },
    forgotPasswordHandler() {
      this.$router.push({ name: "forgotPassword" });
    },
    onFieldChange(field) {
      this.error = null;
      if (field) {
        field.$touch();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/style/mixins";
@import "@/style/variables.scss";

.log-in-page {
  .inner-block {
    padding-top: 32px;
  }

  .loading-spinner {
    text-align: center;
    margin: 0 auto;
  }

  h1 {
    margin-bottom: 32px;
  }

  input {
    margin-top: 0;
  }

  input#password {
    margin-top: 20px;
  }

  input::placeholder {
    font-size: 18px;
    color: #949494;
  }

  .button {
    margin-top: 30px;
    background-color: $orange;
  }

  .section-forgot {
    font-size: 14px;
    margin-top: 5px;
  }

  .forgot-password {
    font-size: 14px;
    font-weight: 700;
    color: $orange;
    border: none;
    background-color: transparent;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
